
import { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Search, Plus, Trash2, Copy, Upload } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export const NumberManager = ({ numbers, onUpdate }: {
  numbers: any[];
  onUpdate: () => void;
}) => {
  const [newNumber, setNewNumber] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>([]);
  const [isBulkAddOpen, setIsBulkAddOpen] = useState(false);
  const [bulkNumbers, setBulkNumbers] = useState("");
  const [isBulkAdding, setIsBulkAdding] = useState(false);

  // Filter numbers based on search query
  const filteredNumbers = useMemo(() => {
    if (!searchQuery) return numbers;
    return numbers.filter(n => n.cert_number.includes(searchQuery));
  }, [numbers, searchQuery]);

  const validateNumber = (num: string) => {
    const validLengths = [3, 6, 8];
    if (!validLengths.includes(num.length)) {
      toast.error("号码长度必须为3位、6位或8位");
      return false;
    }
    if (!/^\d+$/.test(num)) {
      toast.error("号码只能包含数字");
      return false;
    }
    return true;
  };

  const addNumber = async () => {
    if (!newNumber) {
      toast.error("请输入号码");
      return;
    }

    if (!validateNumber(newNumber)) {
      return;
    }

    // Check if number already exists
    const { data: existingNumbers } = await supabase
      .from('cert_applications')
      .select('cert_number')
      .eq('cert_number', newNumber);

    if (existingNumbers && existingNumbers.length > 0) {
      toast.error("该号码已存在");
      return;
    }

    const { error } = await supabase
      .from('cert_applications')
      .insert([
        {
          cert_number: newNumber,
          domain: 'PENDING',
          applicant_name: null,
          applicant_type: 'pending',
          email: 'pending@example.com'
        }
      ]);

    if (error) {
      toast.error("添加失败");
      console.error("Error adding number:", error);
    } else {
      toast.success("添加成功");
      setNewNumber("");
      onUpdate();
    }
  };

  const deleteNumber = async (id: string) => {
    const { error } = await supabase
      .from('cert_applications')
      .delete()
      .eq('id', id);

    if (error) {
      toast.error("删除失败");
      console.error("Error deleting number:", error);
    } else {
      toast.success("删除成功");
      onUpdate();
    }
  };

  const handleBulkAddSubmit = async () => {
    setIsBulkAdding(true);
    
    try {
      const numbersToAdd = bulkNumbers
        .split('\n')
        .map(n => n.trim())
        .filter(n => n && validateNumber(n));
        
      if (numbersToAdd.length === 0) {
        toast.error("没有有效的号码");
        return;
      }
      
      // Check for existing numbers
      const { data: existingNumbers } = await supabase
        .from('cert_applications')
        .select('cert_number')
        .in('cert_number', numbersToAdd);
        
      const existingSet = new Set(existingNumbers?.map(n => n.cert_number) || []);
      const newNumbers = numbersToAdd.filter(n => !existingSet.has(n));
      
      if (newNumbers.length === 0) {
        toast.error("所有号码已存在");
        return;
      }
      
      const { error } = await supabase
        .from('cert_applications')
        .insert(
          newNumbers.map(num => ({
            cert_number: num,
            domain: 'PENDING',
            applicant_name: null,
            applicant_type: 'pending',
            email: 'pending@example.com'
          }))
        );
        
      if (error) {
        toast.error("批量添加失败");
        console.error("Error bulk adding numbers:", error);
      } else {
        toast.success(`成功添加 ${newNumbers.length} 个号码`);
        if (existingSet.size > 0) {
          toast.warning(`${existingSet.size} 个号码已存在，已跳过`);
        }
        setBulkNumbers("");
        setIsBulkAddOpen(false);
        onUpdate();
      }
    } catch (error) {
      console.error("Error in bulk add:", error);
      toast.error("操作失败，请重试");
    } finally {
      setIsBulkAdding(false);
    }
  };

  const toggleSelectNumber = (id: string) => {
    setSelectedNumbers(prev => 
      prev.includes(id) 
        ? prev.filter(n => n !== id) 
        : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedNumbers.length === 0) return;
    
    const { error } = await supabase
      .from('cert_applications')
      .delete()
      .in('id', selectedNumbers);
      
    if (error) {
      toast.error("批量删除失败");
      console.error("Error bulk deleting numbers:", error);
    } else {
      toast.success(`成功删除 ${selectedNumbers.length} 个号码`);
      setSelectedNumbers([]);
      onUpdate();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("已复制到剪贴板");
  };

  const filterNumbersByLength = (length: number) => {
    return filteredNumbers.filter(n => n.cert_number.length === length);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索号码..."
            className="pl-10 bg-white/10 text-white border-white/20"
          />
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <div className="flex gap-2">
            <Input
              value={newNumber}
              onChange={(e) => setNewNumber(e.target.value)}
              placeholder="输入新号码 (3/6/8位)"
              className="w-[180px] bg-white/10 text-white border-white/20"
            />
            <Button 
              onClick={addNumber}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90 whitespace-nowrap"
            >
              <Plus size={16} className="mr-1" /> 添加
            </Button>
          </div>
          
          <Button 
            variant="outline" 
            onClick={() => setIsBulkAddOpen(true)}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <Upload size={16} className="mr-1" /> 批量添加
          </Button>
          
          {selectedNumbers.length > 0 && (
            <Button 
              variant="destructive" 
              onClick={handleDeleteSelected}
              size="sm"
            >
              <Trash2 size={16} className="mr-1" /> 删除选中 ({selectedNumbers.length})
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white/5 p-3 rounded-lg flex justify-between items-center">
        <div className="text-gray-300 text-sm">
          共 <span className="text-white font-medium">{numbers.length}</span> 个号码
          {searchQuery && ` (筛选: ${filteredNumbers.length})`}
        </div>
        <div className="flex gap-2">
          <Badge className="bg-blue-600">{filterNumbersByLength(3).length} 个三位号码</Badge>
          <Badge className="bg-purple-600">{filterNumbersByLength(6).length} 个六位号码</Badge>
          <Badge className="bg-pink-600">{filterNumbersByLength(8).length} 个八位号码</Badge>
        </div>
      </div>

      <Tabs defaultValue="three" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="three">三位靓号</TabsTrigger>
          <TabsTrigger value="six">六位靓号</TabsTrigger>
          <TabsTrigger value="eight">八位靓号</TabsTrigger>
        </TabsList>

        {["three", "six", "eight"].map((length) => (
          <TabsContent key={length} value={length}>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filterNumbersByLength(length === "three" ? 3 : length === "six" ? 6 : 8).map((number) => (
                <div 
                  key={number.id}
                  className={`
                    bg-white/10 p-4 rounded-lg flex justify-between items-center
                    ${selectedNumbers.includes(number.id) ? 'ring-2 ring-blue-500 bg-blue-500/20' : ''}
                    hover:bg-white/15 transition-colors cursor-pointer
                  `}
                  onClick={() => toggleSelectNumber(number.id)}
                >
                  <span className="text-white text-xl font-mono">{number.cert_number}</span>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-300 hover:text-white hover:bg-white/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(number.cert_number);
                      }}
                    >
                      <Copy size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-300 hover:text-red-500 hover:bg-red-500/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNumber(number.id);
                      }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
              
              {filterNumbersByLength(length === "three" ? 3 : length === "six" ? 6 : 8).length === 0 && (
                <div className="col-span-full text-center py-10 text-gray-400">
                  {searchQuery ? "没有找到匹配的号码" : "暂无号码"}
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={isBulkAddOpen} onOpenChange={setIsBulkAddOpen}>
        <DialogContent className="sm:max-w-[600px] bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white">批量添加号码</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-gray-300 text-sm">每行输入一个号码，支持3位、6位或8位纯数字</p>
            <Textarea
              value={bulkNumbers}
              onChange={(e) => setBulkNumbers(e.target.value)}
              placeholder="例如:
123
456
789
111111
222222
33333333"
              className="min-h-[200px] bg-white/10 text-white border-white/20"
            />
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsBulkAddOpen(false)}
              className="border-gray-700 text-gray-300 hover:bg-gray-800"
            >
              取消
            </Button>
            <Button 
              onClick={handleBulkAddSubmit} 
              disabled={isBulkAdding || !bulkNumbers.trim()}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90"
            >
              {isBulkAdding ? '处理中...' : '添加'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
